/**
 * Theme toggle — light/dark mode switch.
 * Persists preference to localStorage.
 * Renders as a fixed button in the bottom-right corner.
 */

let _themeInit = false;
let _zoomInit = false;

/** Apply saved theme — safe to call early, before auth. */
export function initTheme(): void {
  if (_themeInit) return;
  _themeInit = true;
  const saved = localStorage.getItem('theme') || localStorage.getItem('ai_worksheet_theme');
  if (saved === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  }
}

/** Render theme toggle + zoom controls — call after auth succeeds. */
export function initThemeToggle(): void {
  initTheme();
  if (_zoomInit) return;
  _zoomInit = true;

  // Create toggle button
  const btn = document.createElement('button');
  btn.id = 'theme-toggle';
  btn.title = 'Toggle light/dark mode';
  btn.style.cssText = 'position:fixed;bottom:12px;right:12px;z-index:9999;width:28px;height:28px;border-radius:50%;border:1px solid var(--colorBorder);background:var(--colorBgCard);cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:var(--shadowCard);transition:all .2s';
  updateIcon(btn);

  btn.addEventListener('click', () => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
      localStorage.setItem('ai_worksheet_theme', 'light');
    } else {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
      localStorage.setItem('ai_worksheet_theme', 'dark');
    }
    updateIcon(btn);
  });

  document.body.appendChild(btn);

  // Zoom controls
  let zoom = parseInt(localStorage.getItem('zoom') || localStorage.getItem('ai_worksheet_zoom') || '100', 10);
  const zoomWrap = document.createElement('div');
  zoomWrap.style.cssText = 'position:fixed;bottom:12px;right:48px;z-index:9999;display:flex;align-items:center;gap:2px;background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:14px;box-shadow:var(--shadowCard);padding:2px';

  const minusBtn = document.createElement('button');
  minusBtn.textContent = '−';
  minusBtn.style.cssText = 'width:24px;height:24px;border:none;background:none;cursor:pointer;font-size:14px;color:var(--colorTextLight);border-radius:50%;display:flex;align-items:center;justify-content:center';

  const label = document.createElement('span');
  label.style.cssText = 'font-size:10px;color:var(--colorTextMuted);min-width:32px;text-align:center;cursor:pointer;user-select:none';

  const plusBtn = document.createElement('button');
  plusBtn.textContent = '+';
  plusBtn.style.cssText = 'width:24px;height:24px;border:none;background:none;cursor:pointer;font-size:14px;color:var(--colorTextLight);border-radius:50%;display:flex;align-items:center;justify-content:center';

  function applyZoom(): void {
    const appEl = document.getElementById('app');
    if (appEl) appEl.style.zoom = zoom === 100 ? '' : String(zoom / 100);
    label.textContent = zoom + '%';
    localStorage.setItem('zoom', String(zoom));
    localStorage.setItem('ai_worksheet_zoom', String(zoom));
  }

  minusBtn.addEventListener('click', () => { zoom = Math.max(50, zoom - 10); applyZoom(); });
  plusBtn.addEventListener('click', () => { zoom = Math.min(200, zoom + 10); applyZoom(); });
  label.addEventListener('click', () => { zoom = 100; applyZoom(); });

  zoomWrap.appendChild(minusBtn);
  zoomWrap.appendChild(label);
  zoomWrap.appendChild(plusBtn);
  document.body.appendChild(zoomWrap);
  applyZoom();
}

function updateIcon(btn: HTMLElement): void {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  if (isDark) {
    btn.innerHTML = '<svg width="14" height="14" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>';
  } else {
    btn.innerHTML = '<svg width="14" height="14" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>';
  }
}
